/**
 * Centralised endpoint registry.
 * Every API URL lives here — no magic strings in service files.
 */
export const endpoints = {
  health: '/health',

  auth: {
    login:   '/auth/login',
    refresh: '/auth/refresh',
    logout:  '/auth/logout',
    me:      '/auth/me',
    activeTenant: '/auth/active-tenant',
    invitesValidate: '/auth/invites/validate',
    invitesAccept: '/auth/invites/accept',
    forgotPassword: '/auth/forgot-password',
    resetPassword: '/auth/reset-password',
    mfaSetup: '/auth/mfa/setup',
    mfaConfirm: '/auth/mfa/confirm',
    mfaVerify: '/auth/mfa/verify',
    mfaDisable: '/auth/mfa/disable',
    mfaRegenerate: '/auth/mfa/recovery-codes/regenerate',
  },

  permissions: {
    catalog: '/permissions',
  },

  platformUsers: {
    list: '/platform/users',
    invite: '/platform/users/invite',
    disable: (userId: string) => `/platform/users/${userId}/disable`,
    enable: (userId: string) => `/platform/users/${userId}/enable`,
    resetMfa: (userId: string) => `/platform/users/${userId}/reset-mfa`,
  },

  tenantUsers: {
    list: '/tenant-users',
    invite: '/tenant-users/invite',
    disable: (membershipId: string) => `/tenant-users/${membershipId}/disable`,
    enable: (membershipId: string) => `/tenant-users/${membershipId}/enable`,
    remove: (membershipId: string) => `/tenant-users/${membershipId}`,
    resetMfa: (userId: string) => `/tenant-users/${userId}/reset-mfa`,
  },

  platformInvites: {
    list: '/platform/invites',
    resend: (inviteId: string) => `/platform/invites/${inviteId}/resend`,
    acceptLink: (inviteId: string) =>
      `/platform/invites/${inviteId}/accept-link`,
    revoke: (inviteId: string) => `/platform/invites/${inviteId}`,
  },

  tenantInvites: {
    list: '/invites',
    resend: (inviteId: string) => `/invites/${inviteId}/resend`,
    acceptLink: (inviteId: string) => `/invites/${inviteId}/accept-link`,
    revoke: (inviteId: string) => `/invites/${inviteId}`,
  },

  platformAudit: {
    list: '/platform/audit',
  },

  tenantAudit: {
    list: '/audit',
  },

  platformRoles: {
    list: '/platform/roles',
    create: '/platform/roles',
    getById: (roleId: string) => `/platform/roles/${roleId}`,
    update: (roleId: string) => `/platform/roles/${roleId}`,
    delete: (roleId: string) => `/platform/roles/${roleId}`,
    permissions: (roleId: string) => `/platform/roles/${roleId}/permissions`,
  },

  tenantRoles: {
    list: '/roles',
    create: '/roles',
    getById: (roleId: string) => `/roles/${roleId}`,
    update: (roleId: string) => `/roles/${roleId}`,
    delete: (roleId: string) => `/roles/${roleId}`,
    permissions: (roleId: string) => `/roles/${roleId}/permissions`,
    agentGrants: (roleId: string) => `/roles/${roleId}/agent-grants`,
  },

  tenants: {
    list:          '/tenants',
    create:        '/tenants',
    getById:       (id: string) => `/tenants/${id}`,
    update:        (id: string) => `/tenants/${id}`,
    delete:        (id: string) => `/tenants/${id}`,
    suspend:       (id: string) => `/tenants/${id}/suspend`,
    activate:      (id: string) => `/tenants/${id}/activate`,
    stats:         (id: string) => `/tenants/${id}/stats`,
  },

  agents: {
    list:          '/agents',
    listByTenant:  (tenantId: string) => `/tenants/${tenantId}/agents`,
    create:        '/agents',
    configSchema:  '/agents/config/schema',
    resolvedConfig: (agentId: string) => `/agents/${agentId}/config`,
    getById:       (agentId: string) => `/agents/${agentId}`,
    update:        (agentId: string) => `/agents/${agentId}`,
    delete:        (agentId: string) => `/agents/${agentId}`,
    updatePrompt:  (agentId: string) => `/agents/${agentId}/system-prompt`,
    activate:      (agentId: string) => `/agents/${agentId}/activate`,
    deactivate:    (agentId: string) => `/agents/${agentId}/deactivate`,
  },

  agentTools: {
    list:    (agentId: string) => `/agents/${agentId}/tools`,
    binding: (agentId: string, toolName: string) =>
      `/agents/${agentId}/tools/${toolName}`,
  },

  agentConfigVersions: {
    list: (agentId: string) => `/agents/${agentId}/config-versions`,
    compare: (agentId: string) => `/agents/${agentId}/config-versions/compare`,
    getById: (agentId: string, versionId: string) =>
      `/agents/${agentId}/config-versions/${versionId}`,
    restore: (agentId: string, versionId: string) =>
      `/agents/${agentId}/config-versions/${versionId}/restore`,
  },

  tools: {
    catalog:     '/tools/catalog',
    credentials: (agentId: string, toolName: string) =>
      `/agents/${agentId}/tools/${toolName}/credentials`,
  },

  /** Per-agent Google Calendar connect flow (scheduling tool). */
  googleCalendar: {
    authorize: (agentId: string) =>
      `/agents/${agentId}/integrations/google/authorize`,
    status: (agentId: string) =>
      `/agents/${agentId}/integrations/google/status`,
    disconnect: (agentId: string) => `/agents/${agentId}/integrations/google`,
  },

  /** Tenant HubSpot MCP connection (OAuth) — MCP-first Phase 5. */
  hubspotIntegration: {
    authorize: '/integrations/hubspot/authorize',
    status: '/integrations/hubspot/status',
    discover: '/integrations/hubspot/discover',
    disconnect: '/integrations/hubspot',
    /** PUT the tenant's own HubSpot app (client ID + secret). */
    app: '/integrations/hubspot/app',
  },

  /** Per-agent MCP tool catalog + enable/disable (Phase 5 A4). */
  mcpAgentTools: {
    list: (agentId: string) => `/agents/${agentId}/mcp/tools`,
    setEnabled: (agentId: string, toolName: string) =>
      `/agents/${agentId}/mcp/tools/${toolName}`,
  },

  /** Tenant's BYO Vapi API key (voice channel) — metadata-only reads, encrypted server-side. */
  vapiCredentials: '/tenants/self/vapi-credentials',

  /** Tenant / agent OpenAI BYOK credentials (B1.3) — metadata-only reads. */
  llmCredentials: {
    list: '/llm-credentials',
    tenant: '/llm-credentials/tenant',
    validateTenant: '/llm-credentials/tenant/validate',
    agent: (agentId: string) => `/agents/${agentId}/llm-credentials`,
    validateAgent: (agentId: string) =>
      `/agents/${agentId}/llm-credentials/validate`,
  },

  /** Usage limits CRUD + status (B2.1–B2.4). */
  usageLimits: {
    list: '/billing/usage-limits',
    upsert: '/billing/usage-limits',
    remove: (id: string) => `/billing/usage-limits/${id}`,
    status: '/billing/usage/limits/status',
    endUserHealth: '/billing/usage/limits/end-user-health',
  },
  usageLimitMessages: {
    catalog: '/billing/usage-limit-messages/catalog',
    list: '/billing/usage-limit-messages',
    upsert: '/billing/usage-limit-messages',
    remove: (id: string) => `/billing/usage-limit-messages/${id}`,
  },
  platformUsageLimits: {
    list: '/platform/billing/usage-limits',
    upsert: '/platform/billing/usage-limits',
    remove: (id: string) => `/platform/billing/usage-limits/${id}`,
  },

  /** Tenant AI usage analytics + CSV export (B0.4 / B3.1). */
  billingUsage: {
    summary: '/billing/usage/summary',
    byAgent: '/billing/usage/by-agent',
    byOperation: '/billing/usage/by-operation',
    events: '/billing/usage/events',
    exportCsv: '/billing/usage/export.csv',
  },

  /** Derived approaching / exceeded limit alerts (B3.3). */
  billingAlerts: {
    list: '/billing/alerts',
  },

  /** Platform (Super Admin) AI usage analytics (B0.4 / B3.2). */
  platformBillingUsage: {
    summary: '/platform/billing/usage/summary',
    byTenant: '/platform/billing/usage/by-tenant',
    events: '/platform/billing/usage/events',
    exportCsv: '/platform/billing/usage/export.csv',
    credentialsStatus: '/platform/billing/credentials-status',
  },

  platformBillingAlerts: {
    list: '/platform/billing/alerts',
  },

  /** Per-agent voice (Vapi) configuration — the "enable voice agent?" toggle + settings. */
  agentVoiceConfig: {
    get:    (agentId: string) => `/agents/${agentId}/voice`,
    upsert: (agentId: string) => `/agents/${agentId}/voice`,
  },

  // Engine-neutral phone voice profile (Prompt 2.4).
  agentVoiceProfile: {
    profile: (agentId: string) => `/agents/${agentId}/voice/profile`,
    options: (agentId: string) => `/agents/${agentId}/voice/options`,
    preview: (agentId: string) => `/agents/${agentId}/voice/preview`,
  },

  /** Native web voice (beta) per agent (Prompt 4.2): flag + availability. */
  agentNativeWebVoice: {
    get: (agentId: string) => `/agents/${agentId}/voice/native-web`,
    update: (agentId: string) => `/agents/${agentId}/voice/native-web`,
  },

  /** Public bootstrap config for the embeddable web voice widget (V4.3) — no auth. */
  voiceWidget: {
    config: (agentId: string) => `/voice/${agentId}/widget-config`,
    /**
     * Post-call summary + confirmations. Only asked after the widget saw the
     * call end, so it says so (`ended=1`): the backend finishes a web call
     * whose provider never sent its end notice.
     */
    wrapUp: (agentId: string, callId: string) =>
      `/voice/${agentId}/calls/${encodeURIComponent(callId)}/wrap-up?ended=1`,
  },

  /** Public per-agent widget branding/theme (Step 5a) — no auth. */
  widgetBranding: {
    get: (agentId: string) => `/widget/${agentId}/branding`,
  },

  /** Call-logs admin view (V4.4) — tenant-scoped, optional agent/date filters. */
  voiceCalls: {
    list: '/voice-calls',
    timeline: (id: string) => `/voice-calls/${id}/timeline`,
    /** 5-minute signed link to a phone call's recording (Prompt 2.6). */
    recording: (id: string) => `/voice-calls/${id}/recording`,
    /** 5-minute signed link to the caller's voicemail (Prompt 2.7). */
    voicemail: (id: string) => `/voice-calls/${id}/voicemail`,
  },

  // Telephony (Prompt 1.13). Every endpoint is a 404 while the backend runs
  // with TELEPHONY_ENABLED=false.
  phoneNumbers: {
    list: '/phone-numbers',
    available: '/phone-numbers/available',
    byId: (id: string) => `/phone-numbers/${id}`,
    release: (id: string) => `/phone-numbers/${id}/release`,
    reclaim: (id: string) => `/phone-numbers/${id}/reclaim`,
    /** Pay the month and reactivate a suspended number (Prompt 2.10). */
    reactivate: (id: string) => `/phone-numbers/${id}/reactivate`,
    /** Import from the tenant's own Twilio account (Prompt 2.9). */
    import: '/phone-numbers/import',
    reapplyWebhooks: (id: string) => `/phone-numbers/${id}/reapply-webhooks`,
  },
  // 4.3: country catalog, compliance bundles, porting, self-serve BYO.
  telephonyCompliance: {
    countries: '/telephony/countries',
    bundles: '/telephony/regulatory-bundles',
    bundleClone: '/telephony/regulatory-bundles/clone',
    bundle: (id: string) => `/telephony/regulatory-bundles/${id}`,
    bundleDocument: (id: string, type: string) =>
      `/telephony/regulatory-bundles/${id}/documents/${encodeURIComponent(type)}`,
    bundleSubmit: (id: string) => `/telephony/regulatory-bundles/${id}/submit`,
    bundleRefresh: (id: string) => `/telephony/regulatory-bundles/${id}/refresh`,
    ports: '/telephony/port-requests',
    port: (id: string) => `/telephony/port-requests/${id}`,
    portDocument: (id: string, kind: 'loa' | 'bill') =>
      `/telephony/port-requests/${id}/documents/${kind}`,
    portCancel: (id: string) => `/telephony/port-requests/${id}/cancel`,
  },
  platformTelephonyCompliance: {
    countries: '/platform/telephony/countries',
    country: (c: string) => `/platform/telephony/countries/${c}`,
    regulations: (c: string) => `/platform/telephony/countries/${c}/regulations`,
    ports: '/platform/telephony/port-requests',
    port: (id: string) => `/platform/telephony/port-requests/${id}`,
    portDocument: (id: string, kind: 'loa' | 'bill') =>
      `/platform/telephony/port-requests/${id}/documents/${kind}`,
    portPin: (id: string) => `/platform/telephony/port-requests/${id}/reveal-pin`,
    portComplete: (id: string) => `/platform/telephony/port-requests/${id}/complete`,
    byoSelfServe: (tenantId: string) => `/platform/tenants/${tenantId}/telephony/byo-self-serve`,
    // 4.4: Trust Hub / SHAKEN.
    trustHubPrimary: '/platform/telephony/trust-hub',
    trustHub: (tenantId: string) => `/platform/tenants/${tenantId}/telephony/trust-hub`,
    trustHubSubmit: (tenantId: string) =>
      `/platform/tenants/${tenantId}/telephony/trust-hub/submit`,
    trustHubRefresh: (tenantId: string) =>
      `/platform/tenants/${tenantId}/telephony/trust-hub/refresh`,
  },
  // 4.5: Brain Tool API (MCP) keys per agent.
  // Phase L: live calls (SSE stream, detail, takeover / transfer / notes).
  liveCalls: {
    stream: '/live-calls',
    active: '/live-calls/active',
    detail: (id: string) => `/live-calls/${id}`,
    takeover: (id: string) => `/live-calls/${id}/takeover`,
    joined: (id: string) => `/live-calls/${id}/takeover/joined`,
    end: (id: string) => `/live-calls/${id}/takeover/end`,
    transfer: (id: string) => `/live-calls/${id}/transfer`,
    transferOptions: (id: string) => `/live-calls/${id}/transfer-options`,
    notes: (id: string) => `/live-calls/${id}/notes`,
  },
  dataSources: {
    root: (agentId: string) => `/agents/${agentId}/data-sources`,
    connections: (agentId: string) => `/agents/${agentId}/data-sources/connections`,
    connection: (agentId: string, id: string) => `/agents/${agentId}/data-sources/connections/${id}`,
    operation: (agentId: string, id: string) => `/agents/${agentId}/data-sources/operations/${id}`,
    preview: (agentId: string) => `/agents/${agentId}/data-sources/preview`,
  },
  brainApiKeys: {
    list: (agentId: string) => `/agents/${agentId}/brain-api-keys`,
    byId: (agentId: string, id: string) => `/agents/${agentId}/brain-api-keys/${id}`,
  },
  // 4.4: deletion by contact (GDPR-style).
  contacts: {
    lookup: '/contacts/lookup',
    deleteByIdentifier: '/contacts/delete-data',
    deleteData: (id: string) => `/contacts/${id}/delete-data`,
  },
  // Routing policies + caller blocklist (Prompt 2.5).
  routingPolicies: {
    list: '/routing-policies',
    options: '/routing-policies/options',
    byId: (id: string) => `/routing-policies/${id}`,
    simulate: (id: string) => `/routing-policies/${id}/simulate`,
    verify: (id: string, label: string) =>
      `/routing-policies/${id}/destinations/${encodeURIComponent(label)}/verify`,
    confirm: (id: string, label: string) =>
      `/routing-policies/${id}/destinations/${encodeURIComponent(label)}/confirm`,
    /** Transfers per destination, last 30 days (Prompt 3.5). */
    destinationUsage: (id: string) => `/routing-policies/${id}/destinations/usage`,
    testCall: (id: string, label: string) =>
      `/routing-policies/${id}/destinations/${encodeURIComponent(label)}/test-call`,
    consentPreview: (id: string) => `/routing-policies/${id}/consent/preview`,
    consentAudio: (id: string) => `/routing-policies/${id}/consent/audio`,
  },
  callerBlocklist: {
    list: '/caller-blocklist',
    byE164: (e164: string) => `/caller-blocklist/${encodeURIComponent(e164)}`,
  },
  phoneNumberOrders: {
    list: '/phone-number-orders',
    byId: (id: string) => `/phone-number-orders/${id}`,
  },
  telephony: {
    accountSelf: '/telephony/accounts/self',
    /** Recording retention (Prompt 2.6). */
    settings: '/telephony/settings',
    /** The tenant's own Twilio account (Prompt 2.9). */
    byo: '/telephony/accounts/self/byo',
    byoValidate: '/telephony/accounts/self/byo/validate',
    byoIncomingNumbers: '/telephony/byo/incoming-numbers',
    /** Phone usage of one billing period, for Plan & billing (Prompt 2.10). */
    usage: '/telephony/usage',
    /** The plan's phone terms + this month's use (Prompt 2.11). */
    usagePlan: '/telephony/usage/plan',
    /** The tenant's Vapi SIP number for Vapi-engine phone calls (Prompt 3.4). */
    vapiSipNumber: '/telephony/vapi/sip-number',
    /** Phone handoffs: the call + how its transfer ended (Prompt 3.5). */
    handoffOutcomes: '/telephony/handoff-outcomes',
  },
  /** Platform → Tenants → Telephony (Prompt 2.9). */
  platformTenantTelephony: {
    overview: (tenantId: string) => `/platform/tenants/${tenantId}/telephony`,
    byo: (tenantId: string) => `/platform/tenants/${tenantId}/telephony/byo`,
    byoValidate: (tenantId: string) => `/platform/tenants/${tenantId}/telephony/byo/validate`,
  },
  // Platform voice engines page (Prompt 2.3).
  platformVoice: {
    engines: '/platform/voice/engines',
    scorecards: '/platform/voice/engines/scorecards',
  },

  analytics: {
    outcomes: '/analytics/outcomes',
    /** Phone KPIs (Prompt 2.12). */
    phone: '/analytics/phone',
    knowledgeGaps: '/analytics/knowledge-gaps',
    knowledgeAnswers: '/analytics/knowledge-answers',
  },

  handoffs: {
    list:   '/handoffs',
    update: (id: string) => `/handoffs/${id}`,
  },

  bugReports: {
    list:   '/bug-reports',
    getById: (id: string) => `/bug-reports/${id}`,
    approve: (id: string) => `/bug-reports/${id}/approve`,
    reject:  (id: string) => `/bug-reports/${id}/reject`,
  },

  meetings: {
    list:    '/meetings',
    getById: (id: string) => `/meetings/${id}`,
  },

  knowledge: {
    list:    (agentId: string) => `/agents/${agentId}/documents`,
    upload:  (agentId: string) => `/agents/${agentId}/documents`,
    createText: (agentId: string) => `/agents/${agentId}/documents/text`,
    getById: (agentId: string, docId: string) => `/agents/${agentId}/documents/${docId}`,
    delete:  (agentId: string, docId: string) => `/agents/${agentId}/documents/${docId}`,
    retry:   (agentId: string, docId: string) => `/agents/${agentId}/documents/${docId}/retry`,
    status:  (agentId: string, docId: string) => `/agents/${agentId}/documents/${docId}/status`,
    viewUrl: (agentId: string, docId: string) => `/agents/${agentId}/documents/${docId}/view-url`,
  },

  activity: {
    forSession: (agentId: string, sessionId: string) =>
      `/agents/${agentId}/activity/${sessionId}`,
  },

  actions: {
    list: '/actions',
    grouped: '/actions/grouped',
  },

  conversations: {
    list:    (agentId: string) => `/agents/${agentId}/conversations`,
    create:  (agentId: string) => `/agents/${agentId}/conversations`,
    getById: (agentId: string, sessionId: string) => `/agents/${agentId}/conversations/${sessionId}`,
    delete:  (agentId: string, sessionId: string) => `/agents/${agentId}/conversations/${sessionId}`,
    journey: (agentId: string, sessionId: string) => `/agents/${agentId}/conversations/${sessionId}/journey`,
  },

  messages: {
    list: (agentId: string, sessionId: string) =>
      `/agents/${agentId}/conversations/${sessionId}/messages`,
  },

  chat: {
    /** Public endpoint — no agent-scoped path, agentId is in the body */
    message: '/chat/message',
    /** Public SSE stream — same body as `message` (C2.1). */
    messageStream: '/chat/message/stream',
    feedback: '/chat/feedback',
    /** Public session create with widget publishable key (B4.2). */
    sessions: '/chat/sessions',
    /** Public resume — load messages for a visitor-owned session. */
    sessionMessages: (sessionId: string) =>
      `/chat/sessions/${sessionId}/messages`,
  },

  widgetKeys: {
    list: (agentId: string) => `/agents/${agentId}/widget-keys`,
    rotate: (agentId: string) => `/agents/${agentId}/widget-keys/rotate`,
    previewToken: (agentId: string) =>
      `/agents/${agentId}/widget-keys/preview-token`,
    origins: (agentId: string, id: string) =>
      `/agents/${agentId}/widget-keys/${id}/origins`,
    revoke: (agentId: string, id: string) =>
      `/agents/${agentId}/widget-keys/${id}/revoke`,
  },

  // Super-admin platform plan management (platform monetization).
  platformBillingPlans: {
    catalog: '/platform/billing/catalog',
    plans: '/platform/billing/plans',
    plan: (id: string) => `/platform/billing/plans/${id}`,
    planVersions: (id: string) => `/platform/billing/plans/${id}/versions`,
    version: (versionId: string) => `/platform/billing/versions/${versionId}`,
    publishVersion: (versionId: string) =>
      `/platform/billing/versions/${versionId}/publish`,
  },

  // Public, unauthenticated self-service signup.
  publicSignup: {
    plans: '/public/plans',
    signup: '/public/signup',
  },

  // Tenant self-serve billing portal.
  tenantBilling: {
    plans: '/billing/plans',
    subscription: '/billing/subscription',
    serviceability: '/billing/subscription/serviceability',
    wallet: '/billing/wallet',
    changePlan: '/billing/subscription/change-plan',
    topup: '/billing/topup',
    topupStatus: '/billing/topup/status',
    invoices: '/billing/invoices',
  },

  // Super-admin per-tenant subscription + wallet operations.
  platformBillingTenant: {
    subscription: (t: string) => `/platform/billing/subscriptions/${t}`,
    serviceability: (t: string) =>
      `/platform/billing/subscriptions/${t}/serviceability`,
    createSubscription: '/platform/billing/subscriptions',
    changePlan: (t: string) =>
      `/platform/billing/subscriptions/${t}/change-plan`,
    migrate: (t: string) => `/platform/billing/subscriptions/${t}/migrate`,
    extendTrial: (t: string) =>
      `/platform/billing/subscriptions/${t}/extend-trial`,
    status: (t: string) => `/platform/billing/subscriptions/${t}/status`,
    wallet: (t: string) => `/platform/billing/wallets/${t}`,
    grant: (t: string) => `/platform/billing/wallets/${t}/grant`,
    invoices: '/platform/billing/invoices',
  },
} as const;
